import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Gift, Loader2, Check, ShieldAlert, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: "referrer_credit", label: "Referrer credit (₹)", placeholder: "e.g. 100", help: "Wallet credit to the existing user when their referee qualifies." },
  { key: "referee_credit", label: "Referee credit (₹)", placeholder: "e.g. 50", help: "Wallet credit to the new user once they place a qualifying first order." },
  { key: "min_first_order_amount", label: "Min first-order amount (₹)", placeholder: "e.g. 200", help: "Subtotal − discount must be at least this much for credits to release." },
  { key: "min_orders_to_share", label: "Min orders to share code", placeholder: "1", help: "Existing user must have placed at least this many non-cancelled orders." },
  { key: "min_lifetime_spend_to_share", label: "Min lifetime spend to share (₹)", placeholder: "500", help: "Sum of non-cancelled order totals required before they can share." },
  { key: "max_referrals_per_referrer_month", label: "Max referrals / referrer / month", placeholder: "0 = unlimited", help: "Hard cap. Once hit, further referrals get blocked." },
  { key: "max_credits_per_referrer_month", label: "Max credits / referrer / month (₹)", placeholder: "0 = unlimited", help: "Hard cap on referrer earnings per month." },
  { key: "qualification_window_days", label: "Qualification window (days)", placeholder: "30", help: "Days after redemption within which the referee must place their first qualifying order." },
];

export default function AdminReferralProgramPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/settings/referral`, { credentials: "include" });
      setCfg(r.ok ? await r.json() : null);
    } catch { setCfg(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!cfg) return;
    if (cfg.enabled) {
      const missing = ["referrer_credit", "referee_credit", "min_first_order_amount"].filter((k) => !Number(cfg[k]));
      if (missing.length) { toast.error("Set referrer credit, referee credit and min first-order amount before enabling."); return; }
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/settings/referral`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cfg),
      });
      if (!r.ok) throw new Error("Save failed");
      const updated = await r.json();
      setCfg(updated);
      toast.success("Referral program saved");
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-6 flex items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" />
      </div>
    );
  }
  if (!cfg) return null;

  const incomplete = !Number(cfg.referrer_credit) || !Number(cfg.referee_credit) || !Number(cfg.min_first_order_amount);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid="admin-referral-panel">
      <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Gift size={18} />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900">Referral Program</h2>
            <p className="text-xs text-gray-500 mt-0.5">Two-sided wallet credits when a referee places their first qualifying paid order.</p>
            {cfg.updated_at && (
              <p className="text-[10px] text-gray-400 mt-1">Last edited {new Date(cfg.updated_at).toLocaleString()}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded ${cfg.enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
            {cfg.enabled ? "Enabled" : "Paused"}
          </span>
          <Switch
            checked={!!cfg.enabled}
            onCheckedChange={(v) => set("enabled", v)}
            data-testid="referral-enable-toggle"
          />
        </div>
      </div>

      {incomplete && (
        <div className="p-4 bg-amber-50 border-b border-amber-100 flex items-start gap-2 text-xs text-amber-900" data-testid="referral-setup-banner">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>
            <span className="font-bold">Setup required.</span> Set referrer credit, referee credit and min first-order amount before turning the program on. Customers will not see the "Refer & Earn" card until you do.
          </span>
        </div>
      )}

      <div className={`p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 ${cfg.enabled ? "" : "opacity-60"}`}>
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-xs text-gray-500 mb-1 block">{f.label}</Label>
            <Input
              type="number"
              min="0"
              value={cfg[f.key] ?? 0}
              onChange={(e) => set(f.key, Number(e.target.value || 0))}
              placeholder={f.placeholder}
              disabled={!cfg.enabled && f.key !== "enabled"}
              data-testid={`referral-field-${f.key}`}
            />
            <p className="text-[10px] text-gray-400 mt-1">{f.help}</p>
          </div>
        ))}
      </div>

      <div className="px-5 pb-5 flex items-center gap-3 flex-wrap">
        <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="referral-save">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Check className="w-4 h-4 mr-2" />Save</>}
        </Button>
        {cfg.enabled && !incomplete && (
          <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1">
            <ShieldAlert size={12} /> Live now: ₹{cfg.referrer_credit} for referrer / ₹{cfg.referee_credit} for referee on ₹{cfg.min_first_order_amount}+ first orders.
          </span>
        )}
      </div>
    </div>
  );
}
